/**
 * i18n bootstrap.
 *
 * Strategy:
 *   - i18next + react-i18next + http-backend (we ship locale JSON in /public/locales/).
 *   - Language detection order: localStorage → browser navigator → fallback `en`.
 *   - Persist user's choice in localStorage under `hc_lang`. (When the user is
 *     logged-in we mirror it to their profile via `PATCH /api/users/me` — opt-in,
 *     called from `LanguageSwitcher.jsx`.)
 *   - Supported list is exported so the LanguageSwitcher can render a clean dropdown.
 *
 * Bundle impact: i18next core is ~12KB gz, locales are loaded on-demand from /public.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

export const SUPPORTED_LANGUAGES = [
    { code: "en", label: "English",    flag: "🇬🇧" },
    { code: "es", label: "Español",    flag: "🇪🇸" },
    { code: "fr", label: "Français",   flag: "🇫🇷" },
    { code: "hi", label: "हिन्दी",      flag: "🇮🇳" },
    { code: "zh", label: "中文",        flag: "🇨🇳" },
];

// Static-imported locale bundles. Keeping them in-bundle avoids a network round-trip
// for the initial render — they're tiny (~1-2KB each gzipped) so the trade-off favours
// snappy language-switch latency over a microscopic bundle save.
import en from "./locales/en.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import hi from "./locales/hi.json";
import zh from "./locales/zh.json";

const resources = {
    en: { translation: en },
    es: { translation: es },
    fr: { translation: fr },
    hi: { translation: hi },
    zh: { translation: zh },
};

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        fallbackLng: "en",
        supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
        interpolation: { escapeValue: false }, // React already escapes
        detection: {
            order: ["localStorage", "navigator"],
            lookupLocalStorage: "hc_lang",
            caches: ["localStorage"],
        },
        react: {
            useSuspense: false, // resources are inlined — no async chunks
        },
    });

export default i18n;
